// Neuen Kontakt direkt im Zuweisen-Popup der Alten Kundenakten anlegen
// (PO 28.09.2026: „beim Zuweisen muss man auch einen Kontakt erstellen können —
// wenn nicht gefunden, den Suchtext in die Anlage übernehmen — und dann zuerst
// Status (mit Sonstige) und dann Privat | Firma").
//
// Die Reihenfolge ist dieselbe wie auf der Seite „Neuer Kontakt": der
// Baustein `KontaktStatusSchritt` fragt erst den Status, bei „Sonstige" Bereich
// und Rolle, dann Firma | Privat. Seit 01.10.2026 (B2) kommen die Folgefelder
// der Rollen mit — „+ Person mit Beziehung", „+ Unternehmen" / „+ Verein" —
// genau wie in der Schnellanlage der Kontaktseite; das Popup speichert sie
// nach dem Anlegen. Danach nur das Nötigste: Name, bei der Firma die
// Rechtsform, und die Verantwortlichkeit (FO/BO ist Pflicht wie in jeder
// Anlage). Wer mehr eingeben will, öffnet über „+ Weitere Informationen
// erfassen" die vollständige Anlage als Fenster (User 02.10.2026); nach dem
// Übernehmen wird die Akte dem neuen Kontakt zugewiesen. Ohne Fenster-System
// geht es wie bisher per Route mit Rücksprung.
//
// Kontrolliert: der Formularzustand liegt im Popup, das auch anlegt.

import * as React from "react";
import { KontaktStatusSchritt, type KontaktTyp } from "../forms/KontaktStatusSchritt";
import { FieldRow, LabeledInput } from "../forms/formPrimitives";
import { LegalFormSelect } from "../forms/widgets/LegalFormSelect";
import OwnershipSection from "../forms/OwnershipSection";
import { PersonenMitBeziehungInline, UnternehmenInline } from "../relationsInline/StatusFolgefelder";
import type { HouseholdItem, PersonFirmaItem } from "../relationsInline/types";
import { useAuth } from "../../context/AuthContext";
import { rollenAusFeld } from "../../data/kontaktstatusSegment";
import { fetchLegalFormOptions, rechtsformAusName, type LegalFormOption } from "../../utils/legalFormOptions";
import { VollanlageWeiterKnopf } from "../forms/VollanlageWeiter";
import { ownershipAusFormular } from "../relationsInline/persistRelations";
import type { ContactRef } from "../relationsInline/types";

/**
 * Was aus dem Popup in die Vollanlage wandert: Name (bzw. Firmenname und
 * Rechtsform) und der Status. Die Folgefelder (Haushalt, Unternehmen …)
 * speichert das Popup nach der Anlage selbst — wie bei der Schnellanlage.
 */
export function anlageZuVollanlage(
  felder: AnlageFelder,
  typ: KontaktTyp,
): { kontakt: ContactRef; zusatzFelder: Record<string, string> } {
  const f = (k: string) => (felder[k] ?? "").trim();
  const lf = Number(f("rechtsformId"));
  const kontakt: ContactRef =
    typ === "firma"
      ? {
          type: "Firma",
          manual: true,
          lastName: f("firmenname"),
          ...(f("rechtsform") ? { legalForm: f("rechtsform") } : {}),
          ...(lf > 0 ? { legalFormId: lf } : {}),
        }
      : { type: "Person", manual: true, lastName: f("nachname"), firstName: f("vorname") || undefined };
  const zusatzFelder: Record<string, string> = {};
  for (const k of ["kontaktStatus", "kontaktStatusRollen", "kontaktStatusAuch"]) {
    if (f(k)) zusatzFelder[k] = f(k);
  }
  return { kontakt, zusatzFelder };
}

export type AnlageFelder = Record<string, string>;

/** JSON-Liste aus einem Formularfeld; kaputt oder leer → []. */
export const listeAusFeld = <T,>(wert: string | undefined): T[] => {
  if (!wert) return [];
  try {
    const v = JSON.parse(wert);
    return Array.isArray(v) ? (v as T[]) : [];
  } catch {
    return [];
  }
};

export const NeuerKontaktAnlage: React.FC<{
  felder: AnlageFelder;
  set: (k: string, v: string) => void;
  typ: KontaktTyp | null;
  onTyp: (t: KontaktTyp) => void;
  onAbbrechen: () => void;
  /** Ohne Fenster-System: in die vollständige Anlage wechseln (Route mit Rücksprung). */
  onVollstaendig?: () => void;
  /** Die Vollanlage hat den Kontakt angelegt — jetzt zuweisen. */
  onVollanlageAngelegt?: (neu: { id: number; name: string }) => void;
  /** Knopf sperren (z. B. während das Popup speichert). */
  gesperrt?: boolean;
}> = ({ felder, set, typ, onTyp, onAbbrechen, onVollstaendig, onVollanlageAngelegt, gesperrt }) => {
  const { hasPermission } = useAuth();
  const f = (k: string) => felder[k] ?? "";
  const gewaehlteRollen = rollenAusFeld(f("kontaktStatusRollen"));

  // Rechtsform aus dem Firmennamen vorschlagen, solange niemand sie selbst gewählt hat.
  const rechtsformBeruehrt = React.useRef(false);
  const [optionen, setOptionen] = React.useState<LegalFormOption[]>([]);
  React.useEffect(() => {
    let lebt = true;
    fetchLegalFormOptions()
      .then((l) => lebt && setOptionen(l))
      .catch(() => undefined);
    return () => {
      lebt = false;
    };
  }, []);
  const firmenname = f("firmenname");
  React.useEffect(() => {
    if (rechtsformBeruehrt.current || !optionen.length || typ !== "firma") return;
    const { befund, option } = rechtsformAusName(firmenname, optionen);
    if (befund.konfidenz === "SICHER" && option && !befund.trunkiert) {
      set("rechtsform", option.legalForm);
      set("rechtsformId", String(option.id));
    }
    // `set` ist stabil genug; nur Name, Katalog und Typ lösen aus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmenname, optionen, typ]);

  return (
    <div className="ak-anlage">
      <div className="ak-anlage-kopf">
        <strong>Neuen Kontakt anlegen</strong>
        <button type="button" className="ak-link" onClick={onAbbrechen}>
          doch bestehenden suchen
        </button>
      </div>

      <KontaktStatusSchritt
        f={f}
        set={set}
        typ={typ}
        onTyp={onTyp}
        coIntern={hasPermission("cointern:read")}
        ohneBezuege
        folgefelder={(folgen) => (
          <>
            {folgen.includes("person") && (
              <PersonenMitBeziehungInline
                haushalt={listeAusFeld<HouseholdItem>(felder.rel_household)}
                weitere={listeAusFeld<HouseholdItem>(felder.rel_weitere)}
                onHaushalt={(n) => set("rel_household", JSON.stringify(n))}
                onWeitere={(n) => set("rel_weitere", JSON.stringify(n))}
                startImHaushalt={gewaehlteRollen.includes("Haushaltsmitglied")}
                rollen={gewaehlteRollen}
              />
            )}
            {(folgen.includes("unternehmen") || folgen.includes("verein")) && (
              <UnternehmenInline
                value={listeAusFeld<PersonFirmaItem>(felder.rel_personFirma)}
                onChange={(n) => set("rel_personFirma", JSON.stringify(n))}
                verein={!folgen.includes("unternehmen")}
                rollen={gewaehlteRollen}
              />
            )}
          </>
        )}
      />

      {typ === "person" && (
        <FieldRow>
          <LabeledInput label="Vorname" name="akte-neu-vorname" value={f("vorname")} onChange={(v) => set("vorname", v)} />
          <LabeledInput
            label="Nachname"
            name="akte-neu-nachname"
            required
            value={f("nachname")}
            onChange={(v) => set("nachname", v)}
          />
        </FieldRow>
      )}
      {typ === "firma" && (
        <FieldRow>
          <LabeledInput
            label="Firmenname"
            name="akte-neu-firma"
            required
            value={f("firmenname")}
            onChange={(v) => set("firmenname", v)}
          />
          <LegalFormSelect
            label="Rechtsform"
            name="akte-neu-rechtsform"
            required
            value={f("rechtsform")}
            onChange={(v, id) => {
              rechtsformBeruehrt.current = true;
              set("rechtsform", v);
              set("rechtsformId", id ?? "");
            }}
          />
        </FieldRow>
      )}
      {typ && <OwnershipSection formFields={felder} updateField={set} />}
      {typ && (
        <div className="ak-muted" style={{ whiteSpace: "normal" }}>
          Adresse, Kommunikation und weitere Angaben lassen sich danach in der Akte des Kontakts ergänzen
          {onVollanlageAngelegt
            ? " — oder jetzt gleich in der vollständigen Anlage. Die Akte wird nach dem Übernehmen zugewiesen."
            : "."}
        </div>
      )}
      {typ && onVollanlageAngelegt && (
        <VollanlageWeiterKnopf
          kontakt={() => anlageZuVollanlage(felder, typ).kontakt}
          zusatzFelder={anlageZuVollanlage(felder, typ).zusatzFelder}
          eltern={{ ownership: ownershipAusFormular(felder) }}
          disabled={gesperrt}
          ohneFenster={onVollstaendig}
          onCreated={({ contactId, ref }) =>
            onVollanlageAngelegt({ id: contactId, name: ref.displayName || ref.lastName || "Kontakt" })
          }
        />
      )}
    </div>
  );
};
